import type {
    ComposableOptionsBase,
    ProductAttrsGroupOption,
    ProductAttrsGroupSpec,
    ProductAttrsDisabledModel,
    ProductOptionsModel,
    ProductAttrsMaps,
    ProductOrderedOptionsModel,
    ProductOrderOptionAttrs,
    GenericObject,
    ProductAttrsMap,
    Sku,
    SkusMap
} from '~/types';
import {
    getAllCombosFromArrays,
    sortWithSortOrderAscFn,
    arrayAppend
} from 'x1-base/utils';
import { isEqual as lo_isEqual } from 'es-toolkit';
import { omit as lo_omit } from 'es-toolkit/compat';
import {
    createSharedComposable
} from '@vueuse/core';

const topLevelChangeOptionNames = [
    'Stringing'
];

export interface UseProductOptionsOptions extends ComposableOptionsBase {}
export function useProductOptionsStandalone(options: Partial<UseProductOptionsOptions> = {}) {

    function processOptions(
        optionGroups: ProductAttrsGroupOption[],
        attrValUidsFilter: Nilish<string[]>,
        specOptions?: Nilish<ProductAttrsGroupSpec[]>
    ): ProductAttrsGroupOption[] {
        const items = optionGroups.slice(0);

        if (specOptions?.length) {
            const promItems = specOptions.reduce((acc: ProductAttrsGroupOption[], o) => {
                if (o.isProminent) {
                    const newOpt = Object.assign(lo_omit(o, ['isVisible']), {
                        recommendedValueUid: null
                    });

                    acc.push(newOpt);
                }

                return acc;
            }, []);

            arrayAppend(items, promItems)
            items.sort(sortWithSortOrderAscFn<ProductAttrsGroupOption>);
        }

        if (attrValUidsFilter) {
            return items.reduce(
                (
                    acc: ProductAttrsGroupOption[],
                    opt: ProductAttrsGroupOption
                ) => {
                    if (opt.isProminent) {
                        acc.push(opt);
                        return acc;
                    }

                    const attrValExists = opt.groupValues.find(
                        (optVal) => attrValUidsFilter.includes(optVal.value.uid!)
                    );

                    if (attrValExists) {
                        acc.push(opt);
                    }

                    return acc;
                },
                []
            );
        }

        return items;
    }

    function getDisabledOptionsModel(
        keys: string[] | Record<string, unknown>
    ): ProductAttrsDisabledModel {
        if (!Array.isArray(keys)) {
            keys = Object.keys(keys);
        }

        return keys.reduce(
            (
                acc: ProductAttrsDisabledModel,
                key: string
            ) => {
                acc[key] = new Set<string>();
                return acc;
            },
            {}
        );
    }

    function findMissingAttrValues(
        skusMap: SkusMap,
        attrsCombosMap: string[][]
    ): string[][] {
        const skuMapVals = Object.values(skusMap);
        return attrsCombosMap.filter((attrs) => {
            return !skuMapVals.find(
                (skuVals: string[]) => lo_isEqual(skuVals, attrs)
            );
        });
    }

    function createAttrsCombosLookupMap(
        attrsObj: GenericObject<string[]> | ProductAttrsGroupOption[]
    ): string[][] {
        const attrVals = Array.isArray(attrsObj) ?
            attrsObj.map((obj) => obj.groupValues.map((o) => o.value.uid!)) : Object.values(attrsObj);
        return getAllCombosFromArrays<string>(...attrVals);
    }

    function checkProductOrderedOptions(
        attrsMaps: ProductAttrsMaps,
        orderedOptAttrsVals: Ref<ProductOrderOptionAttrs>,
        optionsInvalids: Ref<string[][]>,
        callback: (disableValue: boolean, optionKey: string, value: string) => void
    ): void {
        const { attrs } = attrsMaps;
        const {
            attrs: optionsKeys,
            attrsVals: optionsValues
        } = orderedOptAttrsVals.value;

        optionsKeys.forEach((optKey: string, i: number) => {
            const options = attrs[optKey];

            options?.forEach((optVal: string, j: number) => {
                if (optionsValues[j] !== optVal) {
                    const tempOptionsValues = optionsValues.slice(0);
                    tempOptionsValues[i] = optVal;

                    const disableValue = optionsInvalids.value.some(
                        (invVals: string[]) => lo_isEqual(invVals, tempOptionsValues)
                    );

                    callback(disableValue, optKey, optVal);
                }
            });
        });
    }

    function checkTopLevelChangeOption(
        optionGroups: ProductAttrsGroupOption[]
    ): Nullable<string> {
        const opt = optionGroups[0];
        
        if (opt && topLevelChangeOptionNames.includes(opt.attribute.name)) {
            return opt.attribute.uid;
        }

        return null;
    }

    return {
        processOptions,
        getDisabledOptionsModel,
        findMissingAttrValues,
        createAttrsCombosLookupMap,
        getAllCombosFromArrays,
        checkProductOrderedOptions,
        checkTopLevelChangeOption
    };
}

export const useProductOptions = createSharedComposable(useProductOptionsStandalone);
